import React, { useMemo, useState, useEffect } from "react";
import { Grid, Select, MultiSelect, Text } from "@mantine/core";
import { useForceUpdate } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  DATA_PERSONAL_TRADING_BROKERS,
  DATA_PERSONAL_TRADING_ACCOUNT_TYPE,
  DATA_PERSONAL_TRADING_STATUS,
  DATA_PERSONAL_TRADING_PRODUCT_TYPE,
} from "../data/options";

const brokerValues = (list) =>
  list.map((b) => (typeof b === "string" ? b : b.value));

const sortBrokersByEnglish = (options) =>
  [...options].sort((a, b) =>
    (a.value || "").localeCompare(b.value || "", "en", {
      sensitivity: "base",
    })
  );

const mergeBrokerOptions = (base, ...extraNames) => {
  const seen = new Set(brokerValues(base));
  const merged = [...base];
  for (const name of extraNames) {
    const v = (name || "").trim();
    if (!v || seen.has(v)) continue;
    seen.add(v);
    const preset = DATA_PERSONAL_TRADING_BROKERS.find((b) => b.value === v);
    merged.push(preset ?? { value: v, label: v });
  }
  return sortBrokersByEnglish(merged);
};

const normalizeProductTypes = (content) => {
  if (Array.isArray(content.productTypes) && content.productTypes.length) {
    return content.productTypes;
  }
  if (content.productType) {
    return [content.productType];
  }
  return [];
};

export const PersonalTradingAccountListHeader = () => {
  const { t } = useTranslation();

  return (
    <Grid gutter="xs">
      <Grid.Col span={5}>
        <Text size="xs" weight={500}>
          {t("Broker Name")}
        </Text>
      </Grid.Col>
      <Grid.Col span={2}>
        <Text size="xs" weight={500}>
          {t("Account Type")}
        </Text>
      </Grid.Col>
      <Grid.Col span={2}>
        <Text size="xs" weight={500}>
          {t("Status")}
        </Text>
      </Grid.Col>
      <Grid.Col span={3}>
        <Text size="xs" weight={500}>
          {t("Product Types")}
        </Text>
      </Grid.Col>
    </Grid>
  );
};

const PersonalTradingAccountRow = ({
  item,
  form,
  readOnly = false,
}) => {
  const forceUpdate = useForceUpdate();
  const { t } = useTranslation();
  const content = item.content;

  const siblingBrokers = useMemo(() => {
    const accounts =
      _.get(form?.values, "consultantInfo.personalTradingAccounts") ?? [];
    return accounts.map((a) => a?.brokerName).filter(Boolean);
  }, [form?.values]);

  const [brokerOptions, setBrokerOptions] = useState(() =>
    mergeBrokerOptions(
      DATA_PERSONAL_TRADING_BROKERS,
      content.brokerName,
      ...siblingBrokers
    )
  );

  useEffect(() => {
    setBrokerOptions((cur) =>
      mergeBrokerOptions(
        DATA_PERSONAL_TRADING_BROKERS,
        ...brokerValues(cur),
        content.brokerName,
        ...siblingBrokers
      )
    );
  }, [content.brokerName, siblingBrokers.join("|")]);

  const setField = (field, value) => {
    if (readOnly) return;
    content[field] = value;
    forceUpdate();
  };

  const productTypes = normalizeProductTypes(content);

  const fieldProps = {
    size: "xs",
    readOnly,
  };

  return (
    <Grid gutter="xs" align="center">
      <Grid.Col span={5}>
        <Select
          {...fieldProps}
          data={brokerOptions}
          value={content.brokerName ?? null}
          searchable
          clearable
          creatable
          getCreateLabel={(query) =>
            t("Add broker: {{name}}", { name: query.trim() })
          }
          onCreate={(query) => {
            const v = query.trim();
            if (!v) return null;
            setBrokerOptions((cur) => mergeBrokerOptions(cur, v));
            return v;
          }}
          onChange={(v) => setField("brokerName", v)}
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <Select
          {...fieldProps}
          data={DATA_PERSONAL_TRADING_ACCOUNT_TYPE}
          value={content.accountType ?? null}
          searchable
          clearable
          onChange={(v) => setField("accountType", v)}
        />
      </Grid.Col>
      <Grid.Col span={2}>
        <Select
          {...fieldProps}
          data={DATA_PERSONAL_TRADING_STATUS}
          value={content.status ?? null}
          searchable
          clearable
          onChange={(v) => setField("status", v)}
        />
      </Grid.Col>
      <Grid.Col span={3}>
        <MultiSelect
          {...fieldProps}
          data={DATA_PERSONAL_TRADING_PRODUCT_TYPE}
          value={productTypes}
          searchable
          clearable
          onChange={(v) => {
            setField("productTypes", v ?? []);
            content.productType =
              v?.length === 1 ? v[0] : v?.length ? v[0] : undefined;
          }}
        />
      </Grid.Col>
    </Grid>
  );
};

export default PersonalTradingAccountRow;
